import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "../pages/PurchaseOrders.css";
import "./PickerModal.css";

/* Seçici modalların ortak kabuğu (pk-*): Model / Atölye / Operatör tekli seçim, Teknik çoklu seçim ve Yeni teknik formu bunu kullanır.
   Dil po-* (PurchaseOrders.css); yalnız liste/klavye ayrıntıları PickerModal.css'te. */

export const PI = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>,
};

export const trLower = (s?: string | null) => (s || "").toLocaleLowerCase("tr-TR");
export const dash = <span className="pk-none">—</span>;

/** Esc'i pencerenin yakalama aşamasında alır ve yayılımı keser: seçici, altındaki modal (ör. Sipariş düzenle) document'a bağlı olsa da
    yalnız kendisi kapanır. */
export function useLayerEsc(onEsc: () => void) {
  const ref = useRef(onEsc);
  useEffect(() => { ref.current = onEsc; });
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); ref.current(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, []);
}

/** Modal kapanınca odağı açan öğeye geri verir (ilk render'da, odak modala geçmeden yakalanır). */
export function useRestoreFocus() {
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
}

/** Satır/dizin değişince imleç satırını görünür alana kaydırır. */
export function useScrollCursor(listRef: React.RefObject<HTMLElement | null>, cursor: number) {
  useEffect(() => {
    if (cursor < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-i="${cursor}"]`)?.scrollIntoView({ block: "nearest" });
  }, [listRef, cursor]);
}

export interface PickColumn<T> {
  head: string;
  cell: (t: T) => React.ReactNode;
  /** po-hide-sm / po-hide-md gibi görünürlük sınıfları th ve td'ye birlikte uygulanır. */
  className?: string;
  /** Verilirse, hiçbir kayıtta değer yoksa sütun gizlenir (boş "—" sütunları yer kaplamasın). */
  has?: (t: T) => boolean;
}

export interface PickCreate {
  /** Araç çubuğundaki düğme ("Yeni model"). */
  label: string;
  /** Form bölümünün başlığı. */
  title: string;
  hint?: React.ReactNode;
  fields: React.ReactNode;
  /** Form açılırken arama metniyle ön doldurma için. */
  onOpen?: (query: string) => void;
  onSubmit: () => void;
  saving: boolean;
  error?: string;
  valid: boolean;
  submitLabel?: string;
}

interface SinglePickerProps<T> {
  kicker: string;
  title: string;
  /** Sayaçta kullanılan ad: "model", "atölye". */
  noun: string;
  items: T[];
  loading: boolean;
  loadError?: string;
  onRetry?: () => void;
  rowKey: (t: T) => string;
  /** q küçük harfe çevrilmiş (tr) arama metni. */
  match: (t: T, q: string) => boolean;
  rowLabel: (t: T) => string;
  placeholder: string;
  columns: PickColumn<T>[];
  /** Listenin üstünde bilgi notu (po-note). */
  notice?: React.ReactNode;
  /** Kayıt hiç yokken gösterilecek açıklama. */
  emptyText?: React.ReactNode;
  onPick: (t: T) => void;
  onClose: () => void;
  create?: PickCreate;
}

/** Tekli seçim: arama + tablo (↑↓ gezin, Enter / çift tık seç), satır içi "Yeni …" formu, alt bilgi sayacı + Vazgeç / Seç. */
export function SinglePicker<T>(p: SinglePickerProps<T>) {
  const { items, loading, create } = p;
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(-1);
  const [mode, setMode] = useState<"list" | "create">("list");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const saving = !!create?.saving;
  useRestoreFocus();

  const { match } = p;
  const filtered = useMemo(() => {
    const t = trLower(q.trim());
    return t ? items.filter((i) => match(i, t)) : items;
  }, [items, q, match]);

  // Arama yazılınca ilk eşleşme Enter'a hazır; boş aramada hiçbir satır önceden seçili görünmez
  useEffect(() => { setCursor(q.trim() ? 0 : -1); }, [q]);
  useScrollCursor(listRef, cursor);

  useEffect(() => {
    if (mode === "list") searchRef.current?.focus();
    else formRef.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
  }, [mode]);

  const close = () => { if (!saving) p.onClose(); };
  const toList = () => { if (!saving) setMode("list"); };
  const openCreate = () => { create?.onOpen?.(q.trim()); setMode("create"); };
  useLayerEsc(() => (mode === "create" ? toList() : close()));

  const columns = useMemo(() => p.columns.filter((c) => !c.has || items.some(c.has)), [p.columns, items]);
  const current = cursor >= 0 ? filtered[cursor] : undefined;
  const pick = (t?: T) => { if (t) p.onPick(t); };

  const onKey = (e: React.KeyboardEvent) => {
    if (mode !== "list" || !filtered.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Home" && e.target !== searchRef.current) { e.preventDefault(); setCursor(0); }
    else if (e.key === "End" && e.target !== searchRef.current) { e.preventDefault(); setCursor(filtered.length - 1); }
    else if (e.key === "Enter" && !(e.target as HTMLElement).closest("button") && current) { e.preventDefault(); pick(current); }
  };

  const rowId = (i: number) => `${uid}-r${i}`;
  const titleId = `${uid}-t`;
  const formId = `${uid}-f`;

  return (
    <div className="po-overlay center top pk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className={`po-modal wide pk-modal${mode === "create" ? " fit" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKey}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">{p.kicker}</span>
            <h3 id={titleId}>{mode === "create" && create ? create.title : p.title}</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={close} disabled={saving}>{PI.close}</button>
        </div>

        {mode === "create" && create ? (
          <form id={formId} ref={formRef} className="po-modal-body pk-body" onSubmit={(e) => { e.preventDefault(); if (create.valid && !saving) create.onSubmit(); }}>
            <section className="po-form-sec">
              <h4>Kayıt bilgileri</h4>
              {create.hint && <p className="po-form-hint">{create.hint}</p>}
              {create.error && <div className="po-note err" role="alert">{create.error}</div>}
              {create.fields}
            </section>
          </form>
        ) : (
          <div className="po-modal-body pk-body">
            {p.notice}
            <div className="pk-tools">
              <div className="po-search">
                {PI.search}
                <input
                  ref={searchRef}
                  value={q}
                  placeholder={p.placeholder}
                  onChange={(e) => setQ(e.target.value)}
                  role="combobox"
                  aria-expanded="true"
                  aria-controls={`${uid}-l`}
                  aria-autocomplete="list"
                  aria-activedescendant={current ? rowId(cursor) : undefined}
                  aria-label={p.placeholder}
                />
                {q && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{PI.close}</button>}
              </div>
              {create && <button type="button" className="po-btn" onClick={openCreate}>{PI.plus}{create.label}</button>}
            </div>

            {p.loadError ? (
              <div className="po-note err" role="alert">
                {p.loadError}
                {p.onRetry && <> · <button type="button" className="po-link" onClick={p.onRetry}>Yeniden dene</button></>}
              </div>
            ) : loading ? (
              <div className="po-loading"><span className="po-spin" />Yükleniyor…</div>
            ) : filtered.length === 0 ? (
              <div className="po-blank small pk-blank">
                <span className="po-blank-icon">{PI.inbox}</span>
                <b>{q.trim() ? `"${q.trim()}" ile eşleşen ${p.noun} yok` : `Kayıtlı ${p.noun} yok`}</b>
                {q.trim() ? "Aramayı değiştirin ya da yeni kayıt oluşturun." : p.emptyText}
                {create && <button type="button" className="po-btn primary" onClick={openCreate}>{PI.plus}{q.trim() ? `"${q.trim()}" ile oluştur` : create.label}</button>}
              </div>
            ) : (
              <div className="pk-list" ref={listRef}>
                <table className="po-table small pk-table" role="grid" id={`${uid}-l`} aria-label={p.title}>
                  <thead>
                    <tr>{columns.map((c) => <th key={c.head} className={c.className}>{c.head}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filtered.map((t, i) => (
                      <tr
                        key={p.rowKey(t)}
                        id={rowId(i)}
                        data-i={i}
                        className={i === cursor ? "on" : ""}
                        aria-selected={i === cursor}
                        title={`${p.rowLabel(t)} — çift tıklayarak seçin`}
                        onClick={() => setCursor(i)}
                        onDoubleClick={() => pick(t)}
                      >
                        {columns.map((c) => <td key={c.head} className={c.className}>{c.cell(t)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="po-modal-foot between">
          {mode === "create" && create ? (
            <>
              <span className="po-hint">Kaydedilince bu kayıt seçilir.</span>
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={toList} disabled={saving}>{PI.back}Listeye dön</button>
                <button type="submit" form={formId} className="po-btn primary" disabled={saving || !create.valid}>
                  {saving ? "Kaydediliyor…" : create.submitLabel || "Ekle ve seç"}
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="po-hint pk-foot-info">
                <span>
                  {current ? <>Seçili: <b>{p.rowLabel(current)}</b></> : <><b>{filtered.length}</b>{q.trim() && filtered.length !== items.length ? ` / ${items.length}` : ""} {p.noun}</>}
                </span>
                <span className="pk-keys po-hide-sm"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> seç · <kbd>Esc</kbd> kapat</span>
              </span>
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={close}>Vazgeç</button>
                <button type="button" className="po-btn primary" disabled={!current} onClick={() => pick(current)}>Seç</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
